import React, { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import featureNames from '~/shared/constants/featureFlags';
import useHasFeature from '~/shared/utils/useHasFeature';
import useIsEmpMembership from '~/shared/utils/useIsEmpMembership';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  mobile,
  Collapsible,
  SkeletonLine,
  useProductDetailContext,
  useBreakpoint,
  Separator,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import { useSession } from '~/techstyle-shared/redux-core';

import PlatinumLockedCTA from '../PlatinumLockedCTA';
import ProductDetailAfterpay from '../ProductDetailAfterpay';
import ProductDetailKlarna from '../ProductDetailKlarna/ProductDetailKlarna';
import QuickView from '../QuickView';
import WishlistCTA from '../WishlistCTA';

import CTAErrorMessage from './CTAErrorMessage';
import EMPAddToBag from './EMPAddToBag';
import errorMessages from './errorMessages';
import GuestAddToBag from './GuestAddToBag';
import LoyaltyAddToBag from './LoyaltyAddToBag';
import PreorderMultiOptionCTA from './PreorderMultiOptionCTA';
import ProductDetailCTAContext, {
  useProductDetailCTAContext,
} from './ProductDetailCTAContext';
import UpdateBagCTA from './UpdateBagCTA';
import VIPAddToBag from './VIPAddToBag';
import VIPOptIn from './VIPOptIn';
import WaitlistCTA from './WaitlistCTA';

const SpeedySignupModal = dynamic(() =>
  import(/* webpackChunkName: "SpeedySignupModal" */ '../SpeedySignupModal')
);

const CTAsWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.ctaWrapper};
  ${mobile`
    margin-top: 16px;
  `}
`;

const CTALoadingBox = styled(SkeletonLine)`
  width: 100%;
`;

const CTALoadingContainer = styled.div`
  display: grid;
  grid-template-rows: auto;
  min-height: 200px;
  grid-gap: 20px;
`;

const separatorStyle = css`
  color: ${({ theme }) => theme.colors.separator};
  margin-block: 14px 16px;
`;

const PlacementsContainer = styled.div`
  padding: 10px;
`;

function CTAs({ isLocked, isPlatinumExclusive, tier }) {
  const { isVip } = useMembership();
  const { isLoggedIn } = useSession();
  const { isEmpMembership } = useIsEmpMembership();

  const cart = useCart({ isLoginRequired: true });
  const {
    isWaitlistEligible,
    isPreorderEligible,
    isVIPAddToBagEnabled,
    isLoyaltyItem,
    isDualCTAsEnabled,
    isModalOpen,
    closeModal,
    hasError,
    hasSizeSelectorError,
    setErrorMessage,
    setErrorIcon,
  } = useProductDetailCTAContext();

  const { formatMessage } = useIntl();
  const isSingleCtaVipOptInEnabled = useHasFeature(
    featureNames.SINGLE_CTA_VIP_OPT_IN
  );

  useEffect(() => {
    if (hasSizeSelectorError) {
      setErrorIcon(false);
      setErrorMessage(formatMessage(errorMessages.sizeSelectorError));
    } else if (hasError) {
      setErrorIcon(false);
      setErrorMessage(formatMessage(errorMessages.soldOutError));
    } else {
      setErrorMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, hasSizeSelectorError]);

  const [isLoading, setIsLoading] = useState(true);

  const { isQuickView } = useProductDetailContext();
  const QuickViewContext = QuickView.useContext();
  const secondarySite = useSecondarySite();

  // Because the `isLoading` state flips whenever a change is made
  // to the cart and NOT when the CTA changes, we want to make sure
  // the loading indicator is only shown on the first page load.
  useEffect(() => {
    if (!cart.networkStatus.isLoading) {
      setIsLoading(false);
    }
  }, [cart]);
  // The cart takes a little bit to load and the CTA display can
  // vary considerably. Having the loading indicator saves the user
  // from potentially seeing several different CTA's flash on-screen.
  if (isLoading) {
    return (
      <CTALoadingContainer>
        <CTALoadingBox />
        <CTALoadingBox />
        <CTALoadingBox />
      </CTALoadingContainer>
    );
  }

  if (secondarySite) {
    return <VIPAddToBag />;
  }

  if (isPlatinumExclusive && isLocked) {
    return <PlatinumLockedCTA tier={tier} />;
  }

  if (isWaitlistEligible) {
    return isQuickView ? (
      <Collapsible defaultOpen={false}>
        <WaitlistCTA />
      </Collapsible>
    ) : (
      <WaitlistCTA />
    );
  } else if (isPreorderEligible) {
    return isQuickView ? (
      <Collapsible defaultOpen={false}>
        <PreorderMultiOptionCTA />
      </Collapsible>
    ) : (
      <PreorderMultiOptionCTA />
    );
  } else if (QuickViewContext && QuickViewContext.isCheckout) {
    return (
      <>
        <UpdateBagCTA />
      </>
    );
  } else if (isLoyaltyItem) {
    return <LoyaltyAddToBag />;
  } else if (isVIPAddToBagEnabled) {
    return isVip && isEmpMembership ? <EMPAddToBag /> : <VIPAddToBag />;
  } else if (isDualCTAsEnabled) {
    return isSingleCtaVipOptInEnabled ? (
      <VIPOptIn />
    ) : (
      <>
        <VIPOptIn />
        <GuestAddToBag />
        {!isLoggedIn ? (
          <SpeedySignupModal isOpen={isModalOpen} onExit={closeModal} />
        ) : null}
      </>
    );
  }
}

CTAs.propTypes = {
  isLocked: PropTypes.bool,
  isPlatinumExclusive: PropTypes.bool,
  tier: PropTypes.object,
};

function ProductDetailCTAs({
  onSuccessfulAddToCart,
  isLocked,
  isPlatinumExclusive,
  tier,
  isAfterpayEnabled,
  isKlarnaEnabled,
}) {
  const { isLoggedIn } = useSession();
  const { isMobile } = useBreakpoint();
  const secondarySite = useSecondarySite();
  const productDetailFromCtx = useProductDetailContext();
  const isMysteryBox = productDetailFromCtx.product?.isMysteryBundle;
  const showWishlistCTA =
    isLoggedIn && !isMobile && !secondarySite && !isMysteryBox;

  return (
    <ProductDetailCTAContext onSuccessfulAddToCart={onSuccessfulAddToCart}>
      <CTAsWrapper>
        {/* Disabling this error message for Mystery Box bundles—its structure differs from a
        typical product and causes false positives */}
        {!isMysteryBox && <CTAErrorMessage />}
        <CTAs
          isLocked={isLocked}
          isPlatinumExclusive={isPlatinumExclusive}
          tier={tier}
        />
      </CTAsWrapper>
      <PlacementsContainer>
        {!secondarySite && (
          <>
            {isKlarnaEnabled && <ProductDetailKlarna />}
            {isAfterpayEnabled && <ProductDetailAfterpay />}
          </>
        )}
      </PlacementsContainer>
      {!isMobile && <Separator thickness={1} separatorStyle={separatorStyle} />}
      {showWishlistCTA && <WishlistCTA />}
    </ProductDetailCTAContext>
  );
}

ProductDetailCTAs.propTypes = {
  isAfterpayEnabled: PropTypes.bool,
  isKlarnaEnabled: PropTypes.bool,
  isLocked: PropTypes.bool,
  isPlatinumExclusive: PropTypes.bool,
  onSuccessfulAddToCart: PropTypes.func,
  tier: PropTypes.object,
};

export default ProductDetailCTAs;
